import { RoutePath } from '@/config';

import BackIcon from '../../assets/icons/back.svg?react';
import CurrencyRubIcon from '../../assets/icons/currency-rub.svg?react';
import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { Link } from 'react-router-dom';

import { OrderApi } from '../../api';
import type { OrderItem } from '../../model';
import {
  formatDateRu,
  formatDaysPlural,
  formatPerDose,
  getOrderStatusConfig,
} from '../../utils';

const formatPrice = (price: number) =>
  price.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

interface Props {
  orderId: number;
  /** Куда ведёт кнопка «Вернуться назад». По умолчанию — история заказов. */
  backTo?: string;
}

const normalizeItems = (
  items: OrderItem[] | Record<string, OrderItem> | null | undefined,
): OrderItem[] => {
  if (!items) return [];
  return Array.isArray(items) ? items : Object.values(items);
};

export const DoctorOrderDetails: FC<Props> = ({ orderId, backTo }) => {
  const {
    data: orderDetails,
    isLoading,
    isError,
  } = useQuery({
    enabled: !!orderId,
    queryFn: () => OrderApi.getById(Number(orderId)),
    queryKey: [OrderApi.KEY, 'getById', orderId],
  });

  if (isLoading) {
    return (
      <div className="rounded-[16px] bg-white p-[24px] text-center">
        <div className="text-[#0788f1]/50">Загрузка данных заказа...</div>
      </div>
    );
  }

  if (isError || !orderDetails) {
    return (
      <div className="rounded-[16px] bg-white p-[24px] text-center">
        <div className="text-[#f83d3d]">
          Ошибка при загрузке данных заказа. Попробуйте обновить страницу.
        </div>
      </div>
    );
  }

  const items = normalizeItems(
    orderDetails.items ??
      (orderDetails as { order_items?: OrderItem[] }).order_items,
  );

  const formatMedicineInfo = (item: (typeof items)[0]) => {
    const medicine = (
      item as {
        medicine?: { dosage?: number; dosage_type?: { value?: string } };
      }
    ).medicine;
    const dosage = medicine?.dosage ?? '';
    const dosageType = medicine?.dosage_type?.value ?? '';
    const tablets = item.total_tablets ?? item.totalAmount ?? item.amount;
    return `${dosage} ${dosageType}, ${tablets}шт`;
  };

  /** Все приёмы по позиции: из каждого элемента receipt_medicine (Утро, День и т.д.), при нескольких — через « • ». */
  const formatDaytime = (item: (typeof items)[0]) => {
    const receiptMedicine = (
      item as {
        receipt_medicine?: Array<{
          amount?: number;
          daytime?: { value?: string } | null;
        }>;
      }
    ).receipt_medicine;
    if (!receiptMedicine) return null;
    const list = Array.isArray(receiptMedicine)
      ? receiptMedicine
      : [receiptMedicine];
    const parts = list
      .map((rm) => {
        const value = rm?.daytime?.value;
        if (!value) return null;
        const amount = rm?.amount;
        const prefix = amount != null && amount !== 1 ? `х${amount} ` : '';
        return `${prefix}${value}`;
      })
      .filter(Boolean);
    if (parts.length === 0) return null;
    return parts.join(' • ');
  };

  /** Полная фраза приёма: «Принимать по 2 капсулы 1 раз в день утром до еды в течение 5 дней.» */
  const formatWhenToTake = (item: (typeof items)[0]) => {
    const raw = item as {
      receipt_medicine?: Array<{
        amount?: number;
        duration?: number;
        frequency?: { value?: string };
        daytime?: { value?: string } | null;
        eating?: { value?: string };
      }>;
      medicine?: { form_type?: { value?: string } };
    };
    const list = Array.isArray(raw.receipt_medicine)
      ? raw.receipt_medicine
      : raw.receipt_medicine
      ? [raw.receipt_medicine]
      : [];
    if (list.length === 0) return null;
    const formType = raw.medicine?.form_type?.value ?? '';
    const daytimes: Record<string, string> = {
      Утро: 'утром',
      День: 'днём',
      Вечер: 'вечером',
      Ночь: 'ночью',
    };
    const sentences = list
      .map((rm) => {
        const amount = rm?.amount ?? 1;
        const duration = rm?.duration;
        const frequency = rm?.frequency?.value;
        const daytime = rm?.daytime?.value;
        const eating = rm?.eating?.value;
        const parts: string[] = [];
        parts.push(`Принимать ${formatPerDose(amount, formType)}`);
        parts.push(frequency === 'В день' ? '1 раз в день' : frequency ?? '');
        if (daytime) parts.push(daytimes[daytime] ?? daytime.toLowerCase());
        if (eating) parts.push(eating.toLowerCase());
        if (duration != null) {
          parts.push(`в течение ${duration} ${formatDaysPlural(duration)}`);
        }
        return parts.filter(Boolean).join(' · ') + '.';
      })
      .filter(Boolean);
    return sentences.length > 0 ? sentences.join(' ') : null;
  };

  const deliveryDateStr = orderDetails.delivery_date
    ? formatDateRu(orderDetails.delivery_date, 'd MMMM yyyy')
    : '';
  const statusValue = orderDetails.status?.value ?? '';

  return (
    <div className="w-full overflow-hidden rounded-[16px] bg-white">
      {/* Полоса с кнопкой «Вернуться назад» */}
      <div className="flex h-[74px] w-full items-center bg-[#f1f7fa] px-[24px]">
        <Link
          to={backTo ?? RoutePath.DoctorOrderHistory}
          className="inline-flex items-center gap-[4px] font-['Ubuntu'] text-[20px] font-bold leading-[30px] text-[#151515]"
        >
          <BackIcon className="size-[24px] shrink-0" />
          <span>Вернуться назад</span>
        </Link>
      </div>

      {/* Номер заказа, дата доставки, статус */}
      <div className="flex flex-col items-start gap-[12px] p-[24px]">
        <div className="font-['Ubuntu'] text-[24px] font-bold leading-[36px] text-[#151515]">
          №{orderDetails.id}
          {deliveryDateStr && (
            <>
              <br />
              Доставлен {deliveryDateStr}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 font-['Ubuntu'] text-[16px] leading-[24px] text-[#151515]">
          <span className="text-[#151515]/50">Статус заказа:</span>
          {(() => {
            const cfg = getOrderStatusConfig(statusValue);
            return (
              <span
                className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
              >
                {cfg.label}
              </span>
            );
          })()}
        </div>
        {orderDetails.last_comment?.trim() && (
          <div className="w-full min-w-0 rounded-[12px] bg-[#f7f8f9] p-[16px]">
            <div className="font-['Ubuntu'] text-sm font-normal leading-5 text-[#151515]/50">
              Комментарий
            </div>
            <div className="font-['Ubuntu'] text-base font-bold leading-6 text-[#151515]">
              {orderDetails.last_comment.trim()}
            </div>
          </div>
        )}
      </div>

      {/* Тёмный хедер: Лекарства + Сформировал(а) заказ (белый текст, имя доктора #5fd98e). Как у клиента: короткое имя и whitespace-nowrap, чтобы на мобилке не переносилось. */}
      <div className="flex min-h-[52px] w-full flex-wrap items-center justify-between gap-y-2 rounded-t-[16px] bg-[#e4f0f7] px-[24px] py-[16px]">
        <div className="flex items-center gap-[6px]">
          <span className="font-['Ubuntu'] text-[16px] font-normal leading-[20px] text-[#151515]">
            Лекарства
          </span>
        </div>
        <div className="flex min-w-0 shrink items-center gap-[6px]">
          <span className="shrink-0 whitespace-nowrap font-['Ubuntu'] text-[16px] font-normal leading-[20px] text-[#151515]">
            Сформировал(а) заказ:
          </span>
          <span className="min-w-0 truncate font-['Ubuntu'] text-[16px] font-normal leading-[20px] text-[#5fd98e] underline">
            {(
              orderDetails as {
                doctor?: { surnameAndInitials?: string; fullName?: string };
              }
            ).doctor?.surnameAndInitials ??
              (orderDetails as { doctor?: { fullName?: string } }).doctor
                ?.fullName ??
              '—'}
          </span>
        </div>
      </div>

      {/* Список препаратов */}
      <div className="rounded-b-[16px] bg-[#e4f0f7]">
        <div className="flex flex-col items-start gap-[12px] rounded-[16px] bg-white p-[24px]">
          {items.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center gap-[8px] py-[24px]">
              <div className="w-full text-center font-['Ubuntu'] text-[20px] font-medium leading-[28px] text-[#151515]">
                Заказ формируется
              </div>
              <div className="flex w-full max-w-[560px] flex-col items-center gap-[8px] text-center">
                <span className="font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-70">
                  Фармацевт сейчас подбирает препараты и рассчитывает стоимость.
                </span>
                <span className="font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-70">
                  Цены появятся в ближайшее время — мы уведомим клиента, как
                  только заказ будет готов к оплате.
                </span>
              </div>
            </div>
          ) : (
            <>
              {items.map((item, index) => {
                const whenToTake = formatWhenToTake(item);
                const daytime = formatDaytime(item);
                const medicine = (
                  item as {
                    medicine?: { name?: string; picture?: { image?: string } };
                  }
                ).medicine;
                const itemPrice = (item as { price?: number }).price ?? 0;
                return (
                  <div
                    key={(item as { id?: number }).id ?? index}
                    className="flex w-full flex-col gap-[12px]"
                  >
                    <div className="flex w-full items-center gap-[12px]">
                      <div className="flex w-[20px] shrink-0 justify-start font-['Ubuntu'] text-[16px] font-normal leading-[20px] text-[#151515] opacity-30">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="inline-flex flex-wrap items-start gap-[12px]">
                          <span className="font-['Ubuntu'] text-[16px] font-bold leading-[24px] text-[#151515]">
                            {medicine?.name ?? '—'}
                          </span>
                          <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                            {formatMedicineInfo(item)}
                          </span>
                        </div>
                        {(whenToTake || daytime) && (
                          <div className="mt-[4px] font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-70">
                            {whenToTake ?? daytime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {items.length > 0 && (
            <>
              <div className="h-[.5px] w-full bg-[#151515] opacity-10" />
              {/* Разбивка: Итого, Стоимость препаратов, Доставка, Сборка, НДС — как у клиента */}
              <div className="mt-[24px] flex w-full flex-col gap-[4px]">
                <div className="flex h-[24px] w-full items-center justify-between">
                  <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                    Итого
                  </span>
                  <div className="flex items-center gap-[2px]">
                    <span className="font-['Ubuntu'] text-[20px] font-medium leading-[24px] text-[#151515]">
                      {formatPrice(
                        orderDetails.total_price ?? orderDetails.price ?? 0,
                      )}
                    </span>
                    <CurrencyRubIcon className="size-[20px]" />
                  </div>
                </div>
                <div className="flex h-[24px] w-full items-center justify-between">
                  <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                    Стоимость препаратов
                  </span>
                  <div className="flex items-center gap-[2px]">
                    <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                      {formatPrice(orderDetails.price ?? 0)}
                    </span>
                    <CurrencyRubIcon className="size-[14px]" />
                  </div>
                </div>
                <div className="flex h-[24px] w-full items-center justify-between">
                  <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                    Доставка
                  </span>
                  <div className="flex items-center gap-[2px]">
                    <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                      {formatPrice(orderDetails.delivery_price ?? 0)}
                    </span>
                    <CurrencyRubIcon className="size-[14px]" />
                  </div>
                </div>
                <div className="flex h-[24px] w-full items-center justify-between">
                  <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                    Сборка заказа
                  </span>
                  <div className="flex items-center gap-[2px]">
                    <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                      {formatPrice(orderDetails.package_fee ?? 0)}
                    </span>
                    <CurrencyRubIcon className="size-[14px]" />
                  </div>
                </div>
                <div className="flex h-[24px] w-full items-center justify-between">
                  <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                    В том числе НДС
                  </span>
                  <div className="flex items-center gap-[2px]">
                    <span className="font-['Ubuntu'] text-[16px] font-normal leading-[24px] text-[#151515]">
                      {formatPrice(orderDetails.vat_amount ?? 0)}
                    </span>
                    <CurrencyRubIcon className="size-[14px]" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
